// PASTE #384: canonicalize the client cart to the SERVER / invoice packing.
//
// #383 root cause: the client rendered the packBucket (V3/V4) pack live, but
// the server + boot + invoice use materializeCart (addToBox forceAccept) +
// assignBoxNumbers. Same items, different boxes/sizes/order -> the cart "snapped"
// on reload. DECISION (Alexis, #384 Option A): canonicalize the CLIENT onto the
// server packer so live == reload == invoice, with no reload and no round-trip.
//
// This re-derives the whole cart through the EXACT functions the server/invoice
// use: materializeCart (forceAccept) for the box layout, then assignBoxNumbers
// for the category ordering. Two guards:
//   - NO-DROP: only runs when the variety cache covers every slug (incl DDB-only);
//     otherwise returns the cart untouched (a line can never disappear).
//   - PRICE-PRESERVING: materializeCart re-derives per-stem price from the
//     catalog WITHOUT a clientId (global). We restore each line's own
//     pricePerStem (the client-specific price captured at add time) so
//     canonicalization never resets a client's negotiated/promo pricing.

import type { Cart, CartLine } from "./types";
import { cartToServerItems, materializeCart } from "./sync";
import { recompute } from "./cart";
import { boxAnchorKey } from "./box-anchor";
import { getCartVarietyLookup, cartCacheHasAll } from "./variety-cache";

function priceKey(l: Pick<CartLine, "slug" | "length" | "colorChoice" | "unit" | "boxCode">): string {
  const box = l.unit === "box" ? (l.boxCode ?? "-") : "-";
  return `${l.slug}::${l.length ?? "-"}::${l.colorChoice ?? "-"}::${l.unit}::${box}`;
}

export function canonicalizeCart(cart: Cart): Cart {
  const slugs = Array.from(
    new Set(cart.boxes.flatMap((b) => b.contents.map((l) => l.slug))),
  );
  if (slugs.length === 0) return cart;
  // NO-DROP guard: never pack with an incomplete lookup.
  if (!cartCacheHasAll(slugs)) return cart;

  const lookup = getCartVarietyLookup();

  // PASTE #393 (A): length-less varieties (lengths:[]) carry NO length. Drop any
  // legacy phantom length off stored lines so a stale "Delphinium · 50cm" line
  // collapses to the SAME identity as a null-length one and MERGES - cartToServer
  // Items keys items by length, so without this they stay two items -> two lines.
  const normalized: Cart = {
    ...cart,
    boxes: cart.boxes.map((b) => ({
      ...b,
      contents: b.contents.map((l) => {
        const v = lookup.get(l.slug);
        return v && v.lengths.length === 0 && l.length !== undefined
          ? { ...l, length: undefined }
          : l;
      }),
    })),
  };

  // Capture the client-specific per-line price BEFORE the re-derive (keyed on the
  // normalized length so it matches the materialized output).
  const priceByKey = new Map<string, number>();
  for (const b of normalized.boxes) {
    for (const l of b.contents) {
      if (l.pricePerStem > 0) priceByKey.set(priceKey(l), l.pricePerStem);
    }
  }

  const materialized = materializeCart(cartToServerItems(normalized), lookup);

  // Restore client-specific prices (materialize re-derived them global).
  const restored: Cart = {
    ...materialized,
    boxes: materialized.boxes.map((b) => ({
      ...b,
      contents: b.contents.map((l) => {
        const p = priceByKey.get(priceKey(l));
        return p !== undefined ? { ...l, pricePerStem: p } : l;
      }),
    })),
  };
  const recomputed = recompute(restored);

  // #468 STABLE BOX ORDER: order boxes by the EARLIEST add time of their
  // contents, so a box keeps its visual position across re-materialize and
  // genuinely NEW boxes (later add times) append at the END. This replaces the
  // per-mutation categoryRank re-sort (assignBoxNumbers) that made the box the
  // client is filling jump to the bottom on every add - the top complaint.
  // Tradeoff (accepted, #384): the client's "Box N" order now follows fill
  // order, which can differ from the INVOICE's category order - the server /
  // invoice keeps its own assignBoxNumbers ordering at order time, untouched.
  const boxes = [...recomputed.boxes].sort((a, b) => {
    const ta = earliestAddedAt(a);
    const tb = earliestAddedAt(b);
    if (ta !== tb) return ta < tb ? -1 : 1;
    return boxAnchorKey(a).localeCompare(boxAnchorKey(b));
  });
  return { ...recomputed, boxes };
}

// The earliest add time across a box's lines (its stable position anchor). A box
// with no timestamped line sorts last (deterministic).
function earliestAddedAt(box: Cart["boxes"][number]): string {
  let min: string | null = null;
  for (const l of box.contents) {
    if (l.addedAt && (min === null || l.addedAt < min)) min = l.addedAt;
  }
  return min ?? "~";
}

